import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Spinner, Alert, Button, Row, Col } from 'react-bootstrap';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { FiArrowLeft, FiPrinter } from '../../utils/icons';

// ── Tipos (só os campos que este relatório realmente usa) ────────────────────

interface QP {
  qp_id: number;
  pergunta_text: string;
  ordem: number;
}

interface PlanoAcao {
  qpId: number;
  posicaoOriginal: number; // posição original da pergunta no questionário (1-indexada)
  dataLimite: string | null;
  responsavelNome: string | null;
  descricao: string | null;
}

const formatarData = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR');
};

const RelatorioPlanosAcaoPage: React.FC = () => {
  const { questionarioId } = useParams<{ questionarioId: string }>();
  const navigate = useNavigate();
  const empresaId = useInterfaceStore((state) => state.empresaId);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [questionario, setQuestionario] = useState<{ nome: string; framework?: string } | null>(null);
  const [perguntasPorId, setPerguntasPorId] = useState<Record<number, { texto: string; posicao: number }>>({});
  const [planosRaw, setPlanosRaw] = useState<PlanoAcao[]>([]);

  useEffect(() => {
    if (!questionarioId) return;
    const id = Number(questionarioId);
    setLoading(true);
    setErro(null);

    const load = async () => {
      const [qRes, aRes] = await Promise.all([
        api.getQuestionnaire(id),
        api.getAnswers(id), // sem usuarioId = modo Auditoria: respostas de todos que responderam
      ]);

      if (!qRes.success || !qRes.data) {
        setErro('Questionário não encontrado.');
        setLoading(false);
        return;
      }

      const q = qRes.data as any;
      setQuestionario({ nome: q.nome, framework: q.framework });
      const qps: QP[] = ((q.perguntas || []) as QP[]).slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
      const porId: Record<number, { texto: string; posicao: number }> = {};
      qps.forEach((p, idx) => { porId[p.qp_id] = { texto: p.pergunta_text, posicao: idx + 1 }; });
      setPerguntasPorId(porId);

      const planos: PlanoAcao[] = [];
      if (aRes.success && Array.isArray(aRes.data)) {
        (aRes.data as any[]).forEach(r => {
          const info = porId[r.questionario_pergunta_id];
          if (!info) return;
          // Plano de Ação registrado na própria pergunta (Nível de Controle) — só é
          // exigido/válido quando a resposta ATUAL é NÃO (regra de detalheExigido em
          // AnswerQuestionnaire). Se a pergunta foi respondida NÃO, teve um plano
          // preenchido, e depois o usuário mudou a resposta para SIM/N-A, o campo
          // plano_descricao fica órfão no banco (o formulário só para de mostrá-lo,
          // não limpa o valor) — sem essa checagem o relatório mostrava um plano
          // "fantasma" de uma resposta que não existe mais.
          if (r.plano_descricao && r.resposta_texto === 'NAO') {
            planos.push({
              qpId: r.questionario_pergunta_id,
              posicaoOriginal: info.posicao,
              dataLimite: r.plano_data_limite || null,
              responsavelNome: r.plano_responsavel_nome || null,
              descricao: r.plano_descricao,
            });
          }
          // Planos de Ação registrados em itens de checklist dessa pergunta — mesma
          // regra, por item: só vale se a MARCAÇÃO ATUAL daquele item for NÃO.
          if (r.checklist_respostas) {
            Object.values(r.checklist_respostas as Record<string, any>).forEach((det: any) => {
              if (det?.plano_descricao && det?.resposta === 'NAO') {
                planos.push({
                  qpId: r.questionario_pergunta_id,
                  posicaoOriginal: info.posicao,
                  dataLimite: det.plano_data_limite || null,
                  responsavelNome: det.plano_responsavel_nome || null,
                  descricao: det.plano_descricao,
                });
              }
            });
          }
        });
      }
      setPlanosRaw(planos);
      setLoading(false);
    };

    load().catch(() => { setErro('Erro ao carregar o relatório.'); setLoading(false); });
  }, [questionarioId]);

  useEffect(() => {
    if (!empresaId) { setNomeEmpresa(''); return; }
    api.getCompany(empresaId).then(res => {
      if (res.success && res.data) setNomeEmpresa(res.data.nome_fantasia || res.data.razao_social || '');
    });
  }, [empresaId]);

  // Ordenação: ordem das perguntas no questionário (1.3 do documento) — nenhuma
  // classificação de severidade aqui, diferente do Relatório de Auditoria.
  const planos = useMemo(
    () => planosRaw.slice().sort((a, b) => a.posicaoOriginal - b.posicaoOriginal),
    [planosRaw]
  );

  if (loading) return <Container className="py-5 text-center"><Spinner animation="border" /></Container>;
  if (erro) return <Container className="py-4"><Alert variant="danger">{erro}</Alert></Container>;

  return (
    <Container fluid className="py-3 py-md-4">
      <div className="d-flex align-items-center justify-content-between mb-3 no-print">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/app/relatorios')}>
          <FiArrowLeft /> Voltar
        </Button>
        <Button variant="primary" size="sm" onClick={() => window.print()}>
          <FiPrinter /> Exportar PDF
        </Button>
      </div>

      <div className="print-area">
        <div className="text-center mb-4">
          <h2 className="mb-0">Relatório de Planos de Ação</h2>
          <div className="text-muted">Planos de ação com responsável e prazo.</div>
          <div className="text-muted">
            {questionario?.nome}{questionario?.framework ? ` - ${questionario.framework}` : ''}
          </div>
        </div>

        <Row className="mb-4 align-items-center">
          <Col xs={6}>
            <div className="text-muted small">Empresa</div>
            <div className="fw-medium">{nomeEmpresa || '—'}</div>
          </Col>
          <Col xs={3}>
            <div className="text-muted small">Emitido em</div>
            <div className="fw-medium">{new Date().toLocaleDateString('pt-BR')}</div>
          </Col>
          <Col xs={3} className="text-end">
            <div className="text-muted small">Total de Planos de Ação</div>
            <div className="fw-medium">{planos.length}</div>
          </Col>
        </Row>

        {planos.length === 0 ? (
          <Alert variant="info">Nenhum Plano de Ação registrado neste questionário.</Alert>
        ) : (
          planos.map((p, i) => (
            <div
              key={`${p.qpId}-${i}`}
              className="print-break-inside-avoid mb-3 p-3"
              style={{ border: '1px solid #dee2e6', borderLeft: '4px solid var(--bs-primary)', borderRadius: 6 }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="fw-bold text-uppercase small text-muted">Plano de Ação {String(i + 1).padStart(2, '0')}</span>
                <span className="text-muted small">Pergunta {p.posicaoOriginal} de {Object.keys(perguntasPorId).length}</span>
              </div>

              <div className="mb-2">
                <div className="text-muted small">Pergunta</div>
                <div className="fw-medium">{perguntasPorId[p.qpId]?.texto || ''}</div>
              </div>

              <Row className="mb-2">
                <Col md={6}>
                  <div className="text-muted small">Data Limite</div>
                  <div>{formatarData(p.dataLimite)}</div>
                </Col>
                <Col md={6}>
                  <div className="text-muted small">Responsável</div>
                  <div>{p.responsavelNome || '—'}</div>
                </Col>
              </Row>

              <div>
                <div className="text-muted small">Descrição do Plano de Ação</div>
                <div className="p-2" style={{ background: 'var(--cp-surface-alt)', borderRadius: 4 }}>
                  {p.descricao}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Container>
  );
};

export default RelatorioPlanosAcaoPage;
